import { initialAvatarClass } from "@/components/ui/atoms";
import { cn } from "@/lib/utils";

import { FotoOuIniciais } from "./AvatarDaPessoa";
import { useFotoDaPessoa } from "./fotos-da-equipe";

/**
 * TUT-552 — o avatar do responsável nas listas (Prazos e a aba Prazos do
 * processo): a foto da ficha (desde a TUT-558, vista por toda a equipe), e
 * as iniciais de sempre quando não há foto ou a imagem falha. Mantém a
 * moldura e a âncora `initial-avatar` da lista (o layout do celular a
 * posiciona). TUT-558: o portal o usa em quem respondeu, com `fotoUrl` dada
 * (`urlDaFotoNoPortal`) e `rotulo=""` (o nome está escrito ao lado).
 * TUT-561: o cartão do Kanban (`RodapeDoCartao`) e a Caixa Única (a outra
 * pessoa da direta e quem escreveu cada mensagem) usam este mesmo avatar,
 * decorativo, com a foto da mesma lista; quem é de fora entra com
 * `userId={null}`, e a foto dele nunca é pedida.
 *
 * Nos prazos é o único "quem" da célula: a foto leva o nome como texto
 * alternativo, e as iniciais seguem como texto legível. O rótulo vai na `<img>`, e não na
 * moldura: `aria-label` numa `<span>` sem papel é descartado em silêncio (a
 * trava de `aria-generic.test.ts`).
 */
/**
 * TUT-561 — a medida do mesmo avatar em cada lugar, sem outro primitivo: a
 * linha da lista (Prazos, o portal e as mensagens da Caixa Única), o rodapé do
 * cartão do Kanban, que é uma linha de 0,75rem, e o item da lista da Caixa
 * Única, que ocupa a vaga do ícone do canal (2,25rem). As iniciais ficam em
 * 0,75rem nas três (o piso da P28).
 */
const TAMANHOS = {
  lista: undefined,
  cartao: "h-[1.4rem] w-[1.4rem]",
  conversa: "h-9 w-9",
} as const;

export function AvatarDoResponsavel({
  userId,
  nome,
  iniciais,
  rotulo,
  fotoUrl: dada,
  tamanho = "lista",
}: {
  /** Onde o avatar mora (padrão: a linha da lista). */
  tamanho?: keyof typeof TAMANHOS;
  /** `null` quando o responsável não é uma pessoa da equipe (texto livre). */
  userId: string | null | undefined;
  nome: string;
  iniciais: string;
  /** O texto alternativo da foto, quando não é só o nome (padrão: `nome`; `""`: decorativa). */
  rotulo?: string;
  /**
   * TUT-558 — a URL já decidida por quem chama (o portal, que não tem lista
   * da equipe). Sem ela, a foto sai da lista das fotos da equipe.
   */
  fotoUrl?: string | null;
}) {
  const daLista = useFotoDaPessoa(dada === undefined ? userId : null);
  const fotoUrl = dada === undefined ? daLista : dada;
  return (
    <span className={cn(initialAvatarClass, "shrink-0 overflow-hidden", TAMANHOS[tamanho])}>
      <FotoOuIniciais fotoUrl={fotoUrl} iniciais={iniciais} alt={rotulo ?? nome} />
    </span>
  );
}
